import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { TIER_PRICING } from "@/lib/tier";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { useAuth } from "@/hooks/useAuth";
import { openBillingPortal } from "@/lib/billing";
import { useNavigate } from "react-router-dom";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Public-facing label for the gate target. Maps to TIER_PRICING keys. */
  tier: "Plus" | "Pro";
  feature: string;
  /** Pass from parent to avoid mounting useSubscription() twice on the same screen. */
  isActive?: boolean;
};

export const UpgradeDialog = ({ open, onOpenChange, tier, feature, isActive = false }: Props) => {
  const planKey = tier === "Plus" ? "plus" : "pro";
  const price = TIER_PRICING[planKey];
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const priceId = `${planKey}_${billing}`;

  const startCheckout = async () => {
    if (!user) {
      onOpenChange(false);
      navigate("/auth?next=/pricing");
      return;
    }
    // Already on a paid plan? Switch via the Billing Portal.
    if (isActive) {
      onOpenChange(false);
      await openBillingPortal();
      return;
    }
    setCheckoutOpen(true);
  };

  return (
    <>
      <Dialog open={open && !checkoutOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Upgrade to {tier}
            </DialogTitle>
            <DialogDescription>
              <strong className="text-foreground">{feature}</strong> is available on the {tier} plan.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              className={`w-full flex items-baseline justify-between rounded-md px-2 py-1 text-left transition-colors ${
                billing === "monthly" ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted"
              }`}
            >
              <span className="text-sm text-muted-foreground">Monthly</span>
              <span className="font-serif text-2xl font-semibold">
                ${price.monthly}
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setBilling("yearly")}
              className={`w-full flex items-baseline justify-between rounded-md px-2 py-1 text-left transition-colors ${
                billing === "yearly" ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted"
              }`}
            >
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                Annual <Badge variant="secondary" className="text-[10px]">2 months free</Badge>
              </span>
              <span className="font-serif text-2xl font-semibold">
                ${price.yearly}
                <span className="text-sm font-normal text-muted-foreground">/yr</span>
              </span>
            </button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={startCheckout}>{isActive ? "Manage subscription" : `Upgrade to ${tier}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={checkoutOpen}
        onOpenChange={(o) => {
          setCheckoutOpen(o);
          if (!o) onOpenChange(false);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Complete your subscription</DialogTitle>
          </DialogHeader>
          {checkoutOpen && <StripeEmbeddedCheckout priceId={priceId} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
